<template>
  <div class="container--devices-analytics-v3">
    <main class="main--devices-analytics-v3">
      <div class="card--devices-analytics-v3">
        <div class="d-flex justify-space-between">
          <span class="body-bold-medium neutral-20-dark--text">{{
            $t('message.pages.analyticsV3.retentionv3.title')
          }}</span>
          <span class="body-regular-smaller neutral-40-light--text">{{
            $t('message.pages.analyticsV3.pages.devices.updatedAgo', {
              time: lastUpdatedMinutes,
            })
          }}</span>
        </div>
        <span class="body-regular-smaller neutral-40-light--text">
          {{ $t('message.pages.analyticsV3.retentionv3.subtitle') }}
          <a
            class="body-bold-smaller neutral-30-base--text span-link"
            @click="handleGoToAnalyticsArticle"
          >
            {{ $t('message.commom.learnMore') }}
            <v-icon size="12" color="neutral-30-base">mdi-open-in-new</v-icon>
          </a>
        </span>

        <v-input
          class="search-bar mt-6 mb-2"
          v-model="searchByUrl"
          size="small"
          hide-details
          :placeholder="
            $t('message.pages.analyticsV3.screen_bandwidth.searchvideo')
          "
          @input="doItemsSearch"
          :disabled="loadings.ranking || loadings.retention"
          ><template v-slot:prepend-inner>
            <v-icon class="neutral-60--text" size="16"> mdi-magnify </v-icon>
          </template></v-input
        >

        <BaseTable
          :columns="visibleTableColumns"
          :items="tableItems"
          :showPagination="true"
          :showActionColumn="true"
          :showSummary="false"
          :loading="loadings.ranking"
          :items-per-page="10"
          :show-pagination="true"
          :is-searching="searchByUrl.length > 0"
          @clear-search="clearSearch"
          @update-visible-columns="currentlyVisibleColumns = $event"
          sumField="play"
          @row-click="toggleRow"
        >
          <template #playCell="{ row }">
            <span class="body-regular-small">{{
              Number(row.play).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}</span>
          </template>
          <template #actionHeader />
          <template #actionCell="{ row }">
            <v-icon size="16" class="neutral-40-light--text">
              {{ row.expanded ? 'panda-chev-up' : 'panda-chev-down' }}
            </v-icon>
          </template>
          <template #expandedRow>
            <div
              class="d-flex pb-4 pt-6"
              style="max-height: 340.5px; gap: 16px"
            >
              <div class="d-flex flex-column mr-4" style="min-width: 60%">
                <template v-if="loadings.retention">
                  <div class="chart-container"></div>
                  <div class="d-flex justify-space-between mt-1">
                    <span class="body-label-caption neutral-40-light--text"
                      >0:00:00</span
                    ><span class="body-label-caption neutral-40-light--text"
                      >0:15:00</span
                    ><span class="body-label-caption neutral-40-light--text"
                      >0:30:00</span
                    ><span class="body-label-caption neutral-40-light--text"
                      >0:45:00</span
                    >
                    <span class="body-label-caption neutral-40-light--text"
                      >1:15:00</span
                    ><span class="body-label-caption neutral-40-light--text"
                      >1:30:00</span
                    >
                  </div>
                </template>
                <template v-else>
                  <RetentionChartVideoV3
                    :retentionChartSeries="chartSeries"
                    :m3u8Url="expandedVideo.video.hls"
                    :annotationPoints="chartAnnotationPoints"
                    :key="retentionChartKey"
                  />
                </template>
              </div>
              <div class="d-flex flex-column" style="width: 100%">
                <template v-if="expandedVideo.pitchRetention">
                  <RetentionResultCard
                    :title="
                      $t('message.pages.analyticsV3.retentionv3.retentionPinch')
                    "
                    :percentValue="expandedVideo.pitchRetention.percent"
                    :videoTime="expandedVideo.pitchRetention.time"
                    :key="pitchRetentionKey"
                    color="#3F55EE"
                    class="mb-4"
                    @openModal="openAddPitchModal"
                    :tooltip="
                      $t(
                        'message.pages.analyticsV3.retentionv3.retentionUntilPintchTooltip'
                      )
                    "
                    :disabled="userIsViewer"
                  />
                </template>
                <template v-else>
                  <RetentionCard
                    :title="
                      $t(
                        'message.pages.analyticsV3.retentionv3.retentionUntilPintch'
                      )
                    "
                    :description="
                      $t(
                        'message.pages.analyticsV3.retentionv3.retentionUntilPintchSub'
                      )
                    "
                    class="mb-4"
                    :tooltip="
                      $t(
                        'message.pages.analyticsV3.retentionv3.retentionUntilPintchTooltip'
                      )
                    "
                    @openModal="openAddPitchModal"
                    :loading="loadings.retention"
                    :disabled="userIsViewer"
                  />
                </template>
                <template v-if="expandedVideo.customRetention">
                  <RetentionResultCard
                    :title="
                      $t(
                        'message.pages.analyticsV3.retentionv3.retentionCustom'
                      )
                    "
                    :percentValue="expandedVideo.customRetention.percent"
                    :videoTime="expandedVideo.customRetention.time"
                    :key="customRetentionKey"
                    color="#20C1A4"
                    class="mb-4"
                    @openModal="openAddCustomPitchModal"
                    :tooltip="
                      $t(
                        'message.pages.analyticsV3.retentionv3.retentionPinchTooltip'
                      )
                    "
                    :disabled="userIsViewer"
                  />
                </template>
                <template v-else>
                  <RetentionCard
                    :title="
                      $t(
                        'message.pages.analyticsV3.retentionv3.retentionCustom'
                      )
                    "
                    :description="
                      $i18next.t(
                        'message.pages.analyticsV3.retentionv3.pitches.custom.description'
                      )
                    "
                    :tooltip="
                      $t(
                        'message.pages.analyticsV3.retentionv3.retentionPinchTooltip'
                      )
                    "
                    @openModal="openAddCustomPitchModal"
                    :loading="loadings.retention"
                    :disabled="userIsViewer"
                  />
                </template>
              </div>
            </div>
          </template>
        </BaseTable>
      </div>
    </main>
    <AddPitchModal />
    <AddCustomPitchModal />
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { format } from 'date-fns'
import i18n from '@/translations/lang'
import newAnalyticsApi from '@/services/newAnalyticsApi'
import BaseTable from '@/components/BaseComponents/BaseTable.vue'
import RetentionCard from './Layout/RententionCard.vue'
import AddPitchModal from './modals/AddPitchModal.vue'
import AddCustomPitchModal from './modals/AddCustomPitchModal.vue'
import RetentionChartVideoV3 from '@/views/AnalyticsV3/components/Charts/RetentionChartVideoV3.vue'
import Fuse from 'fuse.js'
import RetentionResultCard from './Layout/RetentionResultCard.vue'
import { exportToCsv } from '@/utils/csvExporter.js'
import { generatePdfBlob } from '@/utils/pdfExporter.js'
import currencyMixin from '../../mixins/currencyMixin'

export default {
  name: 'Retention',
  components: {
    BaseTable,
    RetentionCard,
    AddPitchModal,
    AddCustomPitchModal,
    RetentionChartVideoV3,
    RetentionResultCard,
  },
  mixins: [currencyMixin],
  data() {
    return {
      loadings: {
        ranking: false,
        retention: false,
      },
      errors: {
        fetchError: false,
        shownErrMsg: '',
      },
      lastUpdatedAt: 0,
      lastUpdatedTimeout: null,
      searchByUrl: '',
      videosData: [],
      filteredRankingItems: [],
      expandedVideo: {},
      expandedVideoIndex: -1,
      currentlyVisibleColumns: [],
      analyticsLinks: {
        pt: 'https://help.pandavideo.com/pt-br/article/como-funcionam-as-metricas-do-analytics-na-panda-video-qk2m59/',
        en: 'https://help.pandavideo.com/en-us/article/how-do-analytics-metrics-work-in-panda-video-kinu6m/',
        es: 'https://help.pandavideo.com/es/article/como-funcionan-las-metricas-de-analytics-en-panda-video-cpkcfv/',
      },
      pitchRetentionKey: Math.floor(Math.random() * 50000),
      customRetentionKey: Math.floor(Math.random() * 50000),
      retentionChartKey: Math.floor(Math.random() * 50000),
    }
  },
  computed: {
    ...mapState({
      dateRange: (state) => state.analytics.filters.dateRange,
      selectedVideos: (state) =>
        state.analytics.filters.filterVideos.selectedVideos,
    }),
    ...mapState('user', {
      userAllInfo: 'userAllInfo',
    }),
    userIsViewer() {
      return this.userAllInfo?.role_name === 'viewer'
    },
    currentLanguage() {
      return this.$i18next.language
    },
    tableItems: {
      get() {
        if (this.searchByUrl.length) {
          return this.mapTableItems(this.filteredRankingItems)
        }
        if (!this.videosData.length) return []
        return this.mapTableItems(this.videosData)
      },
      deep: true,
      immediate: true,
    },
    baseTableColumnsThatCanBeHidden() {
      return this.baseTableColumns
    },
    baseTableColumns() {
      return [
        {
          text: this.$t('message.pages.analyticsV3.retentionv3.videoTitle'),

          value: 'title',
        },
        {
          text: this.$t('message.pages.analytics.account.plays'),
          tooltip: this.$t('message.pages.analytics.account.playsTooltip'),
          value: 'play',
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.retentionv3.retentionMedium'
          ),
          tooltip: this.$t(
            'message.pages.analyticsV3.retentionv3.retentionMediumTooltip'
          ),
          value: 'retention',
        },
      ]
    },
    visibleTableColumns() {
      return this.baseTableColumnsThatCanBeHidden.map((col) => ({
        ...col,
        width: 100 / this.baseTableColumns.length + '%',
      }))
    },
    lastUpdatedMinutes() {
      return Math.floor(this.lastUpdatedAt / 60) || 1
    },
    helpArticle() {
      const urls = {
        en: 'https://help.pandavideo.com/en-us/article/how-do-analytics-metrics-work-in-panda-video-kinu6m/',
        pt: 'https://help.pandavideo.com/pt-br/article/como-funcionam-as-metricas-do-analytics-na-panda-video-qk2m59/',
        es: 'https://help.pandavideo.com/es/article/como-funcionan-las-metricas-de-analytics-en-panda-video-cpkcfv/',
      }
      return urls[this.$i18next.resolvedLanguage] || urls.pt
    },
    chartSeries() {
      return [
        {
          name: this.$i18next.t(
            'message.pages.video.metricsPage.retentionRate'
          ),
          data: this.aggregateRetention(this.expandedVideo.retention),
        },
        {
          name: this.$i18next.t('message.pages.analytics.account.uniquePlays'),
          data: this.aggregateRetention(this.expandedVideo.views_data),
        },
      ]
    },
    chartAnnotationPoints() {
      this.retentionChartKey
      const ret = []
      const pitchMarker = this.expandedVideo.pitchRetention
      if (pitchMarker) {
        const xPitchMarker = Math.round(pitchMarker.time / 5) * 5
        const yPitchMarker = this.expandedVideo.retention[xPitchMarker]
        ret.push({
          x: xPitchMarker,
          y: yPitchMarker,
          seriesIndex: 0,
          yAxisIndex: 0,
          marker: {
            size: 6,
            strokeColor: '#fff',
            fillColor: '#3F55EE',
            strokeWidth: 1,
          },
        })
      }
      const customMarker = this.expandedVideo.customRetention
      if (customMarker) {
        const xCustomMarker = Math.round(customMarker.time / 5) * 5
        const yCustomMarker = this.expandedVideo.retention[xCustomMarker]
        ret.push({
          x: xCustomMarker,
          y: yCustomMarker,
          seriesIndex: 0,
          yAxisIndex: 0,
          marker: {
            size: 6,
            strokeColor: '#fff',
            fillColor: '#20C1A4',
            strokeWidth: 1,
          },
        })
      }
      return ret
    },
  },
  mounted() {
    this.handleUpdateRankingTable()
    this.$root.$on('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$on('trigger-pdf-export', this.generatePdf)
    this.$root.$on('trigger-png-export', this.generateAndDownloadPng)
  },
  beforeDestroy() {
    this.clearTimeIntervals()
    this.$root.$off('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$off('trigger-pdf-export', this.generatePdf)
    this.$root.$off('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$off('closeAddCustomPitchModalData')
    this.$root.$off('closeAddPitchModalData')
  },
  watch: {
    dateRange: {
      handler() {
        this.handleUpdateRankingTable()
      },
    },
    selectedVideos: {
      handler() {
        this.handleUpdateRankingTable()
      },
    },
    '$i18next.resolvedLanguage': {
      handler() {
        this.updateTranslations()
      },
    },
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapMutations('analytics', ['UPDATE_ERROR_STATE']),
    handleGoToAnalyticsArticle() {
      const articleLink =
        this.analyticsLinks[this.$i18next.resolvedLanguage] ??
        this.analyticsLinks['en']
      window.open(articleLink, '_blank')
    },
    roundToNearestRetentionKey(time) {
      return Math.round(time / 5) * 5
    },
    openAddPitchModal() {
      this.$root.$emit('openAddPitchModal', this.expandedVideo)
      this.$root.$on('closeAddPitchModalData', (data) => {
        if (!data || !data.time) return
        this.expandedVideo.pitchRetention = {
          time: data.time,
          percent: this.calcVideoRetentionToTime(
            this.expandedVideo.retention,
            data.time
          ),
        }
        this.pitchRetentionKey++
        this.retentionChartKey++
      })
    },
    openAddCustomPitchModal() {
      this.$root.$emit('openAddCustomPitchModal', this.expandedVideo)
      this.$root.$on('closeAddCustomPitchModalData', (data) => {
        if (!data || !data.time) return
        this.expandedVideo.customRetention = {
          time: data.time,
          percent: this.calcVideoRetentionToTime(
            this.expandedVideo.retention,
            data.time
          ),
        }
        this.customRetentionKey++
        this.retentionChartKey++
      })
    },
    mapTableItems(videos) {
      return videos.map((video) => {
        return {
          id: video.id,
          title: video.title,
          play: video.play,
          retention: `${Math.round(video?.retention) ?? 0}%`,
          expanded: video?.expanded || false,
        }
      })
    },
    aggregateRetention(retention, duration) {
      if (this.maxValue === 0) {
        if (Math.max(...Object.values(retention)) === 0) {
          this.maxValue = 10
        } else {
          this.maxValue = Math.max(...Object.values(retention))
        }
      }

      if (Object.keys(retention).length <= 1) {
        const videoSize = duration
        for (let i = 0; i <= videoSize + 5; i += 5) {
          const missingToVideoSize = videoSize - i
          retention[i] = 0
          if (missingToVideoSize < 5) {
            retention[missingToVideoSize] = 0
            break
          }
        }
      }

      return Object.keys(retention)
        .map((item) => ({
          x: Number(item),
          y: retention[item],
        }))
        .sort(function (a, b) {
          return a.x - b.x
        })
    },
    calcVideoRetentionToTime(retention, time) {
      const roundedTime = this.roundToNearestRetentionKey(time)
      const retentionData = retention[roundedTime]
      const maxPlays = Object.values(retention).sort((a, b) => b - a)[0]
      const percent = (retentionData * 100) / maxPlays
      return Math.round(percent)
    },
    async toggleRow(row) {
      this.loadings.retention = true
      this.expandedVideo = {}

      const videoIndex = this.videosData.findIndex(
        (video) => video.id === row.id
      )

      if (
        this.expandedVideoIndex >= 0 &&
        this.videosData[this.expandedVideoIndex]
      ) {
        this.videosData[this.expandedVideoIndex].expanded = false
        this.videosData = [...this.videosData]
      }
      if (videoIndex === this.expandedVideoIndex) {
        this.expandedVideo = {}
        this.expandedVideoIndex = -1
        this.loadings.retention = false
        return
      }

      // eslint-disable-next-line no-extra-boolean-cast
      if (videoIndex !== -1 && this.videosData[videoIndex]) {
        this.videosData[videoIndex].expanded =
          !this.videosData[videoIndex].expanded
      }

      this.videosData = [...this.videosData]

      const { data } = await newAnalyticsApi.getVideoMetricsV2(
        row.id,
        format(this.dateRange.start, 'yyyy-MM-dd'),
        format(this.dateRange.end, 'yyyy-MM-dd')
      )
      this.expandedVideo = data
      if (data.video.retention_markers) {
        const pitchRetention = data.video.retention_markers.find(
          (marker) => marker.type === 'pitch'
        )
        if (pitchRetention) {
          this.expandedVideo.pitchRetention = pitchRetention
          const maxPlays = Object.values(data.retention).sort(
            (a, b) => b - a
          )[0]
          const roundedPitchTime = this.roundToNearestRetentionKey(
            pitchRetention.time
          )
          const pitchPlays = data.retention[roundedPitchTime] || 0
          const percent = (pitchPlays * 100) / maxPlays
          this.expandedVideo.pitchRetention.percent = Math.round(percent)
        }
        const customRetention = data.video.retention_markers.find(
          (marker) => marker.type === 'custom'
        )
        if (customRetention) {
          this.expandedVideo.customRetention = customRetention
          const maxPlays = Object.values(data.retention).sort(
            (a, b) => b - a
          )[0]
          const roundedCustomTime = this.roundToNearestRetentionKey(
            customRetention.time
          )
          const customPlays = data.retention?.[roundedCustomTime] || 0
          const percent = (customPlays * 100) / maxPlays
          this.expandedVideo.customRetention.percent = Math.round(percent)
        }
      }
      this.expandedVideoIndex = videoIndex
      this.loadings.retention = false
    },
    clearSearch() {
      this.searchByUrl = ''
      this.filteredRankingItems = []
    },
    doItemsSearch() {
      let searchValue = this.searchByUrl
      if (searchValue && searchValue.length > 0) {
        let list = JSON.parse(JSON.stringify(this.videosData))
        let fuseOptions = {
          keys: ['title'],
          includeScore: true,
          threshold: 0.4,
        }
        const fuse = new Fuse(list, fuseOptions)
        let searchResult = fuse.search(searchValue)
        const itemsResults = searchResult.map((result) => result.item)
        this.filteredRankingItems = itemsResults
      } else {
        this.filteredRankingItems = []
      }
    },
    formatAboveThousand(value) {
      const format = this.$i18next.resolvedLanguage === 'en' ? 'en-US' : 'pt-BR'
      return value?.toLocaleString(format)
    },
    updateTranslations() {
      return false
    },
    openHelpArticle() {
      window.open(this.helpArticle, '_blank')
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => {
        if (typeof this.errors[e] === 'string') {
          return (this.errors[e] = '')
        }
        this.errors[e] = false
      })
    },
    handleFetchError(err) {
      console.error(err)
      this.errors.fetchError = true
      this.errors.shownErrMsg = i18n.t('message.errors.unexpectedError')
      this.UPDATE_ERROR_STATE(true)
    },
    clearTimeIntervals() {
      clearInterval(this.lastUpdatedTimeout)
      this.lastUpdatedTimeout = null
      this.lastUpdatedAt = 0
    },
    addPlayrate() {
      this.videosData = this.videosData.map((video) => {
        let playRate =
          video.unique_view === 0
            ? 0
            : ((video.unique_play * 100) / video.unique_view).toFixed(1)
        return {
          ...video,
          playRate: playRate,
        }
      })
    },
    async getAccountRanking() {
      this.loadings.ranking = true
      this.clearErrors()
      try {
        const { data } = await newAnalyticsApi.getRanking(
          format(this.dateRange.start, 'yyyy-MM-dd'),
          format(this.dateRange.end, 'yyyy-MM-dd')
        )
        this.videosData = data.filter((video) => video.play > 0 && video.title)
        if (!this.videosData.length) {
          this.loadings.ranking = false
          return
        }
        const {
          data: { videos: retentionData },
        } = await newAnalyticsApi.getAccountRetention(
          format(this.dateRange.start, 'yyyy-MM-dd'),
          format(this.dateRange.end, 'yyyy-MM-dd')
        )
        this.videosData = this.videosData.map((video) => {
          const retention = retentionData.find(
            (retention) =>
              retention.video_external_id === video.video_external_id
          )
          if (!retention) return { ...video, retention: 0 }
          return {
            ...video,
            retention: retention.engagement,
          }
        })
        this.videosData.sort((a, b) => b.view - a.view)
        this.addPlayrate()
        this.doItemsSearch()
      } catch (e) {
        this.loadings.ranking = false
        this.handleFetchError(e)
      }
      this.loadings.ranking = false
    },
    async fetchVideoAnalytics(id) {
      try {
        let { data } = await newAnalyticsApi.getSingleVideoAnalytics(
          id,
          format(this.dateRange.start, 'yyyy-MM-dd'),
          format(this.dateRange.end, 'yyyy-MM-dd')
        )
        return data
      } catch (e) {
        console.error(e)
      }
    },
    async handleUpdateRankingTable() {
      this.clearErrors()
      try {
        if (!this.selectedVideos?.length) {
          await this.getAccountRanking()
          return
        }
        this.loadings.ranking = true
        let videos = []
        for (let video of this.selectedVideos) {
          let ret = await this.fetchVideoAnalytics(video.videoId)
          const {
            data: { retention },
          } = await newAnalyticsApi.getVideoMetricsV2(
            video.videoId,
            format(this.dateRange.start, 'yyyy-MM-dd'),
            format(this.dateRange.end, 'yyyy-MM-dd')
          )
          const engagement = this.getEngagement(retention)
          videos.push({
            videoExternalId: video.videoExternalId,
            play: ret.total.play,
            unique_play: ret.total.unique_play,
            unique_view: ret.total.unique_view,
            view: ret.total.view,
            title: video.title,
            id: video.videoId,
            retention: engagement,
          })
        }
        this.videosData = [...videos]
        if (videos.length) this.videosData.sort((a, b) => b.view - a.view)
        this.addPlayrate()
        this.doItemsSearch()
        this.loadings.ranking = false
      } catch (err) {
        this.handleFetchError(err)
      }
    },
    getEngagement(retention) {
      const initialViewers = retention['0']
      const values = Object.values(retention)
      const sum = values.reduce((acc, val) => acc + val, 0)
      if (!sum) return 0
      const averageRetention = (sum / (values.length * initialViewers)) * 100
      return averageRetention
    },
    handleAddDevicesPlayPercentage() {
      const totalPlays =
        this.summarizedDevicesInfo.desktop.play +
        this.summarizedDevicesInfo.mobile.play +
        this.summarizedDevicesInfo.others.play

      Object.keys(this.summarizedDevicesInfo).forEach((key) => {
        this.summarizedDevicesInfo[key].percentage =
          totalPlays > 0
            ? Math.round(
                (this.summarizedDevicesInfo[key].play / totalPlays) * 100
              )
            : 0
      })
    },
    handleEachDeviceData(data) {
      Object.keys(data).forEach((device) => {
        let category = 'others'

        if (['Windows', 'Mac OS', 'Linux'].includes(device)) {
          category = 'desktop'
        } else if (['iOS', 'Android'].includes(device)) {
          category = 'mobile'
        }

        this.devicesInfo[device] = {
          name: device,
          view: 0,
          play: 0,
          unique_view: 0,
          unique_play: 0,
        }

        Object.values(data[device]).forEach((dateData) => {
          this.devicesInfo[device].view += dateData.view || 0
          this.devicesInfo[device].play += dateData.play || 0
          this.devicesInfo[device].unique_view += dateData.unique_view || 0
          this.devicesInfo[device].unique_play += dateData.unique_play || 0

          this.summarizedDevicesInfo[category].view += dateData.view || 0
          this.summarizedDevicesInfo[category].play += dateData.play || 0
          this.summarizedDevicesInfo[category].unique_view +=
            dateData.unique_view || 0
          this.summarizedDevicesInfo[category].unique_play +=
            dateData.unique_play || 0
        })
      })
    },
    updateChartSeries() {
      this.series = [
        this.summarizedDevicesInfo.desktop.percentage,
        this.summarizedDevicesInfo.mobile.percentage,
        this.summarizedDevicesInfo.others.percentage,
      ]
    },
    generateAndDownloadCsv() {
      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      exportToCsv(
        `analytics-retention-${startDateFormatted}--${endDateFormatted}.csv`,
        this.currentlyVisibleColumns,
        this.tableItems
      )
    },
    async generatePdf() {
      try {
        const headerData = {
          title: this.$t('message.pages.analyticsV3.retentionv3.title'),
          description: this.$t(
            'message.pages.analyticsV3.retentionv3.subtitle'
          ),
          metrics: [],
        }

        const sortedDataForPdf = [...this.tableItems].sort(
          (a, b) => (b.play || 0) - (a.play || 0)
        )

        const tableHeaders = this.baseTableColumns.map((col) => col.text)

        const tableBody = sortedDataForPdf.map((item) =>
          this.baseTableColumns.map((col) => item[col.value] || '-')
        )

        const columnStyles = {
          0: { cellWidth: 'auto', overflow: 'ellipsize' },
          1: { cellWidth: 40 },
          2: { cellWidth: 40 },
        }

        const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
        const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
        const filename = `retention-${startDateFormatted}--${endDateFormatted}.pdf`

        const pdfBlob = await generatePdfBlob({
          chartHeaderData: headerData,
          tableHeaders: tableHeaders,
          tableBody: tableBody,
          columnStyles: columnStyles,
        })

        this.$root.$emit('pdf-ready', { blob: pdfBlob, filename: filename })
      } catch (error) {
        console.error('Erro ao gerar PDF:', error)
        this.UPDATE_ERROR_STATE(true)
      }
    },
    generateAndDownloadPng() {
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: this.$t('message.pages.analyticsV3.retentionv3.errorPng'),
        type: 'error',
      })
    },
  },
}
</script>

<style scoped lang="scss">
.container--devices-analytics-v3 {
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
}

.main--devices-analytics-v3 {
  flex: 1;
  overflow: auto;
}

.card--devices-analytics-v3 {
  background: varFigma(neutral-100-white);
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.card--table-devices-analytics-v3 {
  background: varFigma(neutral-100-white);
  border-radius: 8px;
  padding: 24px;
  margin-top: 16px;
}

::v-deep .base-table {
  thead tr {
    .header-cell:first-of-type {
      padding-left: 8px !important;
    }
  }
}

.chart-container {
  width: 100%;
  min-height: 280px;
  background-color: #edeff0;
  border-radius: 4px;
}

.span-link:hover {
  text-decoration: underline !important;
}
</style>
